import { Controller } from '@hotwired/stimulus'
import {
  buildArticleViewsChart,
  buildDeviceBreakdownChart,
  buildBotVsHumanChart,
  buildBotTypeChart,
  buildReferrerSourcesChart
} from '~/javascript/charts'

export default class extends Controller {
  static targets = [
    'viewsCanvas',
    'deviceCanvas',
    'botHumanCanvas',
    'botTypeCanvas',
    'referrerCanvas',
    'topReferrers',
    'recentViews',
    'loading',
    'error',
    'summary'
  ]

  static values = {
    url: String,
    days: { type: Number, default: 30 }
  }

  charts = {}

  connect() {
    this.loadAnalytics()
  }

  disconnect() {
    this.destroyCharts()
  }

  destroyCharts() {
    Object.values(this.charts).forEach((chart) => chart.destroy())
    this.charts = {}
  }

  async loadAnalytics() {
    this.showLoading()
    this.hideError()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('days', this.daysValue)

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.renderAll(data)
    } catch (error) {
      console.error('Failed to load analytics:', error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  renderAll(data) {
    this.renderSummary(data.summary)
    this.renderCharts(data.charts)
    this.renderTables(data.tables)
  }

  renderSummary(summary) {
    if (!this.hasSummaryTarget) return

    this.summaryTarget.innerHTML = `
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">TOTAL_VIEWS</p>
          <p class="text-2xl font-bold text-slate-900">${summary.total_views.toLocaleString()}</p>
        </div>
        <div>
          <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">UNIQUE_VISITORS</p>
          <p class="text-2xl font-bold text-slate-900">${summary.unique_visitors.toLocaleString()}</p>
        </div>
        <div>
          <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">HUMAN_VIEWS</p>
          <p class="text-2xl font-bold text-green-600">${summary.human_views.toLocaleString()}</p>
        </div>
        <div>
          <p class="text-xs font-medium text-slate-500 uppercase tracking-wider">BOT_VIEWS</p>
          <p class="text-2xl font-bold text-slate-500">${summary.bot_views.toLocaleString()}</p>
        </div>
      </div>
    `
  }

  renderCharts(charts) {
    this.destroyCharts()

    if (this.hasViewsCanvasTarget && charts.views_over_time) {
      this.charts.views = buildArticleViewsChart(this.viewsCanvasTarget, charts.views_over_time)
    }

    if (this.hasDeviceCanvasTarget && charts.device_breakdown) {
      this.charts.device = buildDeviceBreakdownChart(this.deviceCanvasTarget, charts.device_breakdown)
    }

    if (this.hasBotHumanCanvasTarget && charts.bot_vs_human) {
      this.charts.botHuman = buildBotVsHumanChart(this.botHumanCanvasTarget, charts.bot_vs_human)
    }

    if (this.hasBotTypeCanvasTarget && charts.bot_type_breakdown) {
      this.charts.botType = buildBotTypeChart(this.botTypeCanvasTarget, charts.bot_type_breakdown)
    }

    if (this.hasReferrerCanvasTarget && charts.referrer_sources) {
      this.charts.referrer = buildReferrerSourcesChart(this.referrerCanvasTarget, charts.referrer_sources)
    }
  }

  renderTables(tables) {
    this.renderTopReferrers(tables.top_referrer_domains)
    this.renderRecentViews(tables.recent_views)
  }

  renderTopReferrers(domains) {
    if (!this.hasTopReferrersTarget) return

    if (!domains || domains.length === 0) {
      this.topReferrersTarget.innerHTML = '<p class="text-sm text-slate-500">No referrer data available</p>'
      return
    }

    this.topReferrersTarget.innerHTML = `
      <table class="min-w-full text-sm">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">DOMAIN</th>
            <th class="text-right py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">VIEWS</th>
          </tr>
        </thead>
        <tbody>
          ${domains.map((d, i) => `
            <tr class="${i > 0 ? 'border-t border-slate-200' : ''}">
              <td class="py-2 text-slate-900">${this.escapeHtml(d.domain)}</td>
              <td class="py-2 text-right font-medium text-slate-900">${d.views.toLocaleString()}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderRecentViews(views) {
    if (!this.hasRecentViewsTarget) return

    if (!views || views.length === 0) {
      this.recentViewsTarget.innerHTML = '<p class="text-sm text-slate-500">No recent views</p>'
      return
    }

    this.recentViewsTarget.innerHTML = `
      <table class="min-w-full text-xs">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left py-2 font-semibold text-slate-500 uppercase tracking-wider">TIME</th>
            <th class="text-left py-2 font-semibold text-slate-500 uppercase tracking-wider">TYPE</th>
            <th class="text-left py-2 font-semibold text-slate-500 uppercase tracking-wider">DEVICE</th>
            <th class="text-left py-2 font-semibold text-slate-500 uppercase tracking-wider">SOURCE</th>
          </tr>
        </thead>
        <tbody>
          ${views.map((v, i) => `
            <tr class="${i > 0 ? 'border-t border-slate-200' : ''}">
              <td class="py-2 text-slate-600">${this.formatDate(v.viewed_at)}</td>
              <td class="py-2">
                ${v.is_bot ?
    `<span class="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs">${v.bot_name || 'BOT'}</span>` :
    '<span class="px-2 py-0.5 bg-green-100 text-green-800 text-xs">HUMAN</span>'
}
              </td>
              <td class="py-2 text-slate-900">${v.device_type} / ${v.platform}</td>
              <td class="py-2 text-slate-600">${v.referrer_source}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  changeDays(event) {
    this.daysValue = parseInt(event.target.value, 10)
    this.loadAnalytics()
  }

  formatDate(isoString) {
    const date = new Date(isoString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }
}
